/**
 * One chart panel with tab pills (docs/market-ux.md §4) and a range shared by every tab:
 *
 *   Price      one line per venue for the selected side, consensus now as a dashed line (price
 *              history carries no consensus series — FRICTION.md), your fills as dots
 *   Depth      cumulative size vs price for both sides, from the order book (depth:read)
 *   Best       which venue was cheapest over time (a step line coloured by venue) over a band
 *              showing how far apart the venues were (max − min): the market-making gap
 *   Liquidity  the size available at each venue's best price over time (price-history `available`)
 *
 * Price / Best / Liquidity come from one GET /contests/:id/price-history call for both sides
 * (pricing:history); a side toggle picks which side they draw.
 */
import { useMemo, useState } from "react";
import { bestVenueOverTime, depthCurve, ladderLevels, marketHolding, RANGES, spreadBand, venueLines, venueTimeline, type RangeKey } from "../../lib/marketView.js";
import { fmtCents, fmtPrice, fmtSize } from "../../lib/format.js";
import { useOddsFormat } from "../../lib/odds.js";
import { SCOPES } from "../../lib/omauth.js";
import { VENUE_FOLD } from "../../lib/venueColors.js";
import { Pill, Skeleton } from "../kit.js";
import { ErrorState } from "../ui.js";
import { DepthChart, Legend, TimeChart, type BestSeg, type ChartLine } from "./MarketCharts.js";
import { ScopeLock, useVenueColors } from "./OrderBookBlock.js";
import { BlockCard, type MarketCtx } from "./MarketScreen.js";

type Tab = "price" | "depth" | "best" | "liquidity";
const TABS: { key: Tab; label: string }[] = [
  { key: "price", label: "Price" },
  { key: "depth", label: "Depth" },
  { key: "best", label: "Best" },
  { key: "liquidity", label: "Liquidity" },
];
/** The two sides on the Depth tab (kept off the venue hues' meaning: this is side identity). */
export const SIDE_COLORS = ["#4c9ffe", "#ff9d5c"] as const;
const CONSENSUS = "#aab3c2";
const H = 220;

export function ChartsBlock({ ctx }: { ctx: MarketCtx }) {
  const [tab, setTab] = useState<Tab>("price");
  const [range, setRange] = useState<RangeKey>("6h");
  const pricing = ctx.source.useAccess(SCOPES.pricing);
  const depthAccess = ctx.source.useAccess(SCOPES.depth);
  return (
    <BlockCard
      title="Charts"
      right={
        <div className="flex items-center gap-1" role="tablist" aria-label="Chart">
          {TABS.map((t) => (
            <Pill key={t.key} active={tab === t.key} onClick={() => setTab(t.key)} className="!px-2.5">
              {t.label}
            </Pill>
          ))}
        </div>
      }
    >
      {tab !== "depth" && (
        <div className="flex flex-wrap items-center gap-1.5">
          {ctx.sides.map((s, i) => (
            <Pill key={s.positionHash} active={ctx.side === i} onClick={() => ctx.setSide(i)} className="max-w-[45%]">
              <span className="block truncate">{s.label}</span>
            </Pill>
          ))}
        </div>
      )}
      {tab === "depth" ? (
        depthAccess.ok ? (
          <DepthTab ctx={ctx} />
        ) : (
          <ScopeLock source={ctx.source} access={depthAccess} scope={SCOPES.depth} />
        )
      ) : pricing.ok ? (
        <HistoryTab ctx={ctx} tab={tab} range={range} />
      ) : (
        <ScopeLock source={ctx.source} access={pricing} scope={SCOPES.pricing} />
      )}
      <div className="flex items-center gap-1.5 border-t border-border/70 pt-2" role="group" aria-label="Range">
        {RANGES.map((r) => (
          <Pill key={r.key} active={range === r.key} onClick={() => setRange(r.key)} className={tab === "depth" ? "opacity-50" : ""} title={tab === "depth" ? "The order book is a snapshot (now)" : undefined}>
            {r.label}
          </Pill>
        ))}
        {tab === "depth" && <span className="ml-auto text-[11px] text-faint">now</span>}
      </div>
    </BlockCard>
  );
}

function ChartSkeleton() {
  return <Skeleton className="h-[220px] w-full" />;
}

function Empty({ text }: { text: string }) {
  return (
    <div className="grid place-items-center text-[13px] text-dim" style={{ height: H }}>
      {text}
    </div>
  );
}

function HistoryTab({ ctx, tab, range }: { ctx: MarketCtx; tab: Exclude<Tab, "depth">; range: RangeKey }) {
  const { format } = useOddsFormat();
  const colors = useVenueColors(ctx);
  const hashes = useMemo(() => ctx.sides.map((s) => s.positionHash), [ctx.sides]);
  const q = ctx.source.usePriceHistory(ctx.contestId, hashes, range);
  const scope = ctx.source.useScope();
  const ccy = scope.mode === "real" ? "USD" : "ATLAS";
  const mine = ctx.source.useMyOrders(ccy);
  const side = ctx.sides[ctx.side];
  const r = RANGES.find((x) => x.key === range) ?? RANGES[1];
  // The window ends now; "All" starts at the first point rather than a week of empty axis.
  const [t0, t1] = useMemo(() => {
    const now = Date.now();
    let start = now - r.ms;
    if (range === "all") {
      const ts = (q.points ?? []).map((p) => p.timestamp).filter((t) => t >= start);
      if (ts.length) start = Math.min(...ts);
    }
    return [start, now];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.points, range]);
  const colorOf = (id: string) => colors.get(id) ?? VENUE_FOLD;
  const priceFmt = (v: number) => (format === "cents" ? fmtCents(v) : fmtPrice(v, null, format));

  const model = useMemo(() => {
    const pts = q.points ?? [];
    if (tab === "best") {
      const tl = venueTimeline(pts, side.positionHash, ctx.allowed);
      const best: BestSeg[] = bestVenueOverTime(tl).map((b) => ({ t: b.t, v: b.price, id: b.id, label: b.name, color: colorOf(b.id) }));
      return { best, band: spreadBand(tl), lines: [] as ChartLine[] };
    }
    const lines: ChartLine[] = venueLines(pts, side.positionHash, tab === "price" ? "price" : "available", ctx.allowed).map((l) => ({
      id: l.id,
      label: l.name,
      color: colorOf(l.id),
      points: l.points,
    }));
    return { lines, best: [] as BestSeg[], band: [] as { t: number; lo: number; hi: number }[] };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.points, tab, side.positionHash, ctx.allowed, colors]);

  if (q.isError) return <ErrorState error={q.error} onRetry={q.refetch} />;
  if (q.isLoading) return <ChartSkeleton />;
  const nothing = tab === "best" ? model.best.length === 0 : model.lines.length === 0;
  if (nothing) return <Empty text="No price history in this window" />;

  if (tab === "best") {
    const leaders = [...new Map(model.best.map((b) => [b.id, b])).values()].sort((a, b) => a.label.localeCompare(b.label));
    const now = model.best.at(-1);
    const gap = model.band.at(-1);
    return (
      <>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <Legend items={[...leaders.map((b) => ({ id: b.id, label: b.label, color: b.color })), { id: "band", label: "Venues apart", color: "#8b93a7", dot: true }]} />
          <span className="nums ml-auto text-[11px] text-faint">
            {now && (
              <>
                Best now <span className="text-ink">{now.label} {priceFmt(now.v)}</span>
              </>
            )}
            {gap && <> · {((gap.hi - gap.lo) * 100).toFixed(1)}¢ apart</>}
          </span>
        </div>
        <TimeChart t0={t0} t1={t1} best={model.best} band={model.band} fmt={priceFmt} height={H} label={`Best venue over time, ${side.label}`} />
      </>
    );
  }

  if (tab === "liquidity") {
    return (
      <>
        <Legend items={model.lines.map((l) => ({ id: l.id, label: l.label, color: l.color }))} />
        <TimeChart t0={t0} t1={t1} lines={model.lines} fmt={(v) => fmtSize(v)} floor0 height={H} label={`Size at best price per venue, ${side.label}`} />
      </>
    );
  }

  // Price: venue lines, consensus now, your fills.
  const consensus = side.consensus;
  const fills = marketHolding(mine.items, hashes)
    .fills.filter((f) => f.col === ctx.side)
    .map((f) => ({ t: f.t, v: f.price, label: `You: ${f.contracts.toFixed(2)} at ${priceFmt(f.price)}` }));
  return (
    <>
      <Legend
        items={[
          ...model.lines.map((l) => ({ id: l.id, label: l.label, color: l.color })),
          ...(consensus != null ? [{ id: "fair", label: "Consensus now", color: CONSENSUS, dashed: true }] : []),
          ...(fills.length ? [{ id: "you", label: "Your fills", color: "var(--color-amber)", dot: true }] : []),
        ]}
      />
      <TimeChart
        t0={t0}
        t1={t1}
        lines={model.lines}
        refs={consensus != null ? [{ label: "Consensus now", v: consensus, color: CONSENSUS }] : []}
        dots={fills}
        fmt={priceFmt}
        height={H}
        label={`Price by venue, ${side.label}`}
      />
    </>
  );
}

function DepthTab({ ctx }: { ctx: MarketCtx }) {
  const { format } = useOddsFormat();
  const a = ctx.source.useDepth(ctx.sides[0].positionHash);
  const b = ctx.source.useDepth(ctx.sides[1].positionHash);
  const sides = useMemo(
    () =>
      [a, b].map((q, i) => ({
        id: ctx.sides[i].positionHash,
        label: ctx.sides[i].label,
        color: SIDE_COLORS[i],
        curve: depthCurve(ladderLevels(q.data?.partners ?? [], ctx.allowed)),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [a.data, b.data, ctx.allowed, ctx.sides],
  );
  if (a.isError) return <ErrorState error={a.error} onRetry={a.refetch} />;
  if (a.isLoading || b.isLoading) return <ChartSkeleton />;
  if (sides.every((s) => s.curve.length === 0)) return <Empty text="No order book right now" />;
  return (
    <>
      <Legend items={sides.map((s) => ({ id: s.id, label: s.label, color: s.color }))} />
      <DepthChart sides={sides} fmtPrice={(p) => (format === "cents" ? fmtCents(p) : fmtPrice(p, null, format))} fmtSize={fmtSize} height={H} />
    </>
  );
}
